const STORAGE_KEY = 'life_manager_data';

export const defaultData = {
  budget: {
    income: [],
    expenses: [],
    savings: [],
    expenseCategories: [
      { id: 'food', name: 'Food & Dining', color: '#ef4444', subCategories: ['McDonalds', 'Burger King', 'Supermarket', 'Restaurant'] },
      { id: 'transport', name: 'Transportation', color: '#f97316', subCategories: ['Gas', 'Public Transport', 'Taxi', 'Parking'] },
      { id: 'utilities', name: 'Utilities', color: '#eab308', subCategories: ['Electricity', 'Water', 'Internet', 'Phone'] },
      { id: 'entertainment', name: 'Entertainment', color: '#22c55e', subCategories: ['Movies', 'Games', 'Streaming', 'Events'] },
      { id: 'health', name: 'Health & Fitness', color: '#06b6d4', subCategories: ['Gym', 'Pharmacy', 'Doctor', 'Supplements'] },
      { id: 'shopping', name: 'Shopping', color: '#8b5cf6', subCategories: ['Clothing', 'Electronics', 'Home', 'Personal Care'] },
      { id: 'other', name: 'Other', color: '#6b7280', subCategories: [] }
    ],
    incomeCategories: [
      { id: 'salary', name: 'Salary', color: '#22c55e', subCategories: ['Monthly', 'Bonus', 'Overtime'] },
      { id: 'freelance', name: 'Freelance', color: '#3b82f6', subCategories: ['Design', 'Development', 'Consulting'] },
      { id: 'investments', name: 'Investments', color: '#8b5cf6', subCategories: ['Dividends', 'Interest', 'Capital Gains'] },
      { id: 'gifts', name: 'Gifts & Refunds', color: '#f97316', subCategories: ['Birthday', 'Refund', 'Other'] },
      { id: 'other_income', name: 'Other Income', color: '#6b7280', subCategories: [] }
    ],
    savingsCategories: [
      { id: 'emergency', name: 'Emergency Fund', color: '#ef4444', subCategories: [] },
      { id: 'vacation', name: 'Vacation', color: '#3b82f6', subCategories: ['Summer', 'Winter', 'Weekend'] },
      { id: 'investment', name: 'Investment', color: '#8b5cf6', subCategories: ['Stocks', 'Crypto', 'Real Estate'] },
      { id: 'big_purchase', name: 'Big Purchase', color: '#f97316', subCategories: ['Car', 'House', 'Electronics'] },
      { id: 'other_savings', name: 'Other Savings', color: '#6b7280', subCategories: [] }
    ],
    savingsAccounts: []
  },
  fitness: {
    sports: [],
    personalBests: []
  },
  calendar: {
    events: [],
    birthdays: [],
    sharedCalendars: [],
    birthdaySettings: { linkedToCalendar: false, categoryId: 'purple' },
    categories: [
      { id: 'red', name: 'Red', color: '#ef4444', isCustom: false },
      { id: 'orange', name: 'Orange', color: '#f97316', isCustom: false },
      { id: 'yellow', name: 'Yellow', color: '#eab308', isCustom: false },
      { id: 'green', name: 'Green', color: '#22c55e', isCustom: false },
      { id: 'teal', name: 'Teal', color: '#14b8a6', isCustom: false },
      { id: 'cyan', name: 'Cyan', color: '#06b6d4', isCustom: false },
      { id: 'blue', name: 'Blue', color: '#3b82f6', isCustom: false },
      { id: 'indigo', name: 'Indigo', color: '#6366f1', isCustom: false },
      { id: 'purple', name: 'Purple', color: '#a855f7', isCustom: false },
      { id: 'pink', name: 'Pink', color: '#ec4899', isCustom: false },
      { id: 'lightpink', name: 'Light Pink', color: '#f9a8d4', isCustom: false },
      { id: 'gray', name: 'Gray', color: '#9ca3af', isCustom: false },
      { id: 'black', name: 'Black', color: '#1f2937', isCustom: false }
    ]
  },
  shopping: {
    recipes: [],
    items: [],
    sharedLists: []
  },
  todo: {
    tasks: [],
    groups: []
  },
  timetracker: {
    companies: [],
    entries: []
  },
  diary: {
    entries: []
  },
  settings: {
    currency: 'USD',
    theme: 'light',
    enabledFeatures: {
      dashboard: true,
      calendar: true,
      birthdays: true,
      todo: true,
      reminders: true,
      budget: true,
      savings: true,
      fitness: true,
      sports: true,
      diary: true,
      habits: true,
      timetracker: true,
      shopping: true
    },
    groupOrder: ['dashboard', 'planning', 'finance', 'fitness', 'personal', 'work', 'shopping'],
    showPersonalCalendar: true
  }
};

export const loadData = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      const merged = { ...defaultData, ...parsed };
      // Migrate calendar categories
      const cats = merged.calendar?.categories;
      if (!cats || cats.length === 0 || cats[0].isCustom === undefined) {
        // Old format (or missing) - replace entirely
        merged.calendar = {
          ...merged.calendar,
          events: merged.calendar?.events || [],
          categories: defaultData.calendar.categories
        };
      } else {
        // New format - ensure any new default labels are added at the end
        const existingIds = new Set(cats.map((c) => c.id));
        const missing = defaultData.calendar.categories.filter((c) => !existingIds.has(c.id));
        if (missing.length > 0) {
          merged.calendar = {
            ...merged.calendar,
            categories: [...cats, ...missing]
          };
        }
      }
      // Migrate settings: ensure enabledFeatures + groupOrder exist
      const settings = merged.settings || {};
      const enabled = { ...defaultData.settings.enabledFeatures, ...(settings.enabledFeatures || {}) };
      let order = Array.isArray(settings.groupOrder) && settings.groupOrder.length > 0
        ? [...settings.groupOrder]
        : [...defaultData.settings.groupOrder];
      // Rename old group ids to new ones if present
      const groupRenames = { calendar: 'planning', budget: 'finance' };
      order = order.map((id) => groupRenames[id] || id);
      // Drop duplicates while preserving order
      order = order.filter((id, i) => order.indexOf(id) === i);
      // Append any default groups that aren't yet in saved order
      for (const gid of defaultData.settings.groupOrder) {
        if (!order.includes(gid)) order.push(gid);
      }
      merged.settings = {
        ...defaultData.settings,
        ...settings,
        enabledFeatures: enabled,
        groupOrder: order
      };
      return merged;
    }
    return defaultData;
  } catch (error) {
    console.error('Error loading data:', error);
    return defaultData;
  }
};

export const saveData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch (error) {
    console.error('Error saving data:', error);
    return false;
  }
};

export const exportToJSON = (data) => {
  const dataStr = JSON.stringify(data, null, 2);
  const blob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `life_manager_backup_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const importFromJSON = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        resolve(data);
      } catch (error) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsText(file);
  });
};
